import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

export function ChangePasswordDialog({ open, forced, onClose }) {
  const { refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
      await refresh();
      onClose?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !forced) onClose?.(); }}>
      <DialogContent className={`bg-white ${forced ? "[&>button]:hidden" : ""}`} data-testid="change-password-dialog"
        onInteractOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 brand-text" />{forced ? "Set a new password" : "Change password"}</DialogTitle>
          <DialogDescription>{forced ? "For your security, please replace your temporary password before you continue." : "Update the password you use to sign in."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">{forced ? "Temporary password" : "Current password"}</Label><Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
          <div><Label className="text-xs">New password</Label><Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" /></div>
          <div><Label className="text-xs">Confirm new password</Label><Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="cp-submit" onClick={submit} disabled={busy || !cur || !next} className="brand-bg text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
